<template>
  <div>
    <!-- Table Container Card -->
    <b-card class="mb-0" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              cols="12"
              md="6"
          >
            <label>Affichage</label>
            <v-select
                v-model="perPage"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                class="per-page-selector d-inline-block mx-50"
            />
            <label>Départements</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  placeholder="Rechercher..."
              />
              <b-button v-if="$can('create_department', 'Départements')" variant="primary" @click="openCreateModal">
                <span class="text-nowrap">Ajouter un département</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-alert :show="!!errors" class="mx-2" variant="danger">
        {{ errors }}
        <b-button :disabled="loading || saving" variant="link" @click="fetchDepartmentsList">Réessayer</b-button>
      </b-alert>

      <b-table
          ref="refDepartmentsListTable"
          :fields="tableColumns"
          :items="fetchDepartmentsList"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          class="position-relative"
          empty-text="Pas de département trouvé"
          primary-key="uid"
          responsive
          show-empty
      >
        <!-- A virtual column -->
        <template #cell(index)="data">
          {{ data.index + 1 }}
        </template>
        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <feather-icon
                v-if="$can('edit_department', 'Départements')"
                :id="`department-row-${data.item.id}-edit-icon`"
                class="cursor-pointer text-warning"
                icon="EditIcon"
                size="16"
                @click="
                $router.push({
                  name: 'apps-departments-edit',
                  params: { id: data.item.uid }
                })
              "
            />
            <b-tooltip
                :target="`department-row-${data.item.uid}-edit-icon`"
                class="cursor-pointer"
                title="Modifier"
            />

            <feather-icon
                v-if="$can('delete_department', 'Départements')"
                :id="`department-row-${data.item.uid}-delete-icon`"
                class="mx-1 text-danger"
                icon="TrashIcon"
                size="16"
                @click="deleteDepartment(data.item.uid)"
            />
            <b-tooltip
                :target="`department-row-${data.item.uid}-delete-icon`"
                title="Supprimer Marque"
            />
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
              cols="12"
              sm="6"
          >
            <span class="text-muted"
            >Affichage de {{ dataMeta.from }} à {{ dataMeta.to }} sur
              {{ dataMeta.of }} marques</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
              cols="12"
              sm="6"
          >
            <b-pagination
                v-model="currentPage"
                :per-page="perPage"
                :total-rows="totalDepartments"
                class="mb-0 mt-1 mt-sm-0"
                first-number
                last-number
                next-class="next-item"
                prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-modal v-model="showCreateModal" :hide-header-close="saving" :no-close-on-backdrop="saving"
             :no-close-on-esc="saving"
             :title="editingUid ? 'Modifier le département' : 'Ajouter un département'" hide-footer
             @hide="preventSavingClose">
      <b-form @submit.prevent="saveDepartment">
        <b-form-group label="Nom" label-for="department-name">
          <b-form-input id="department-name" v-model="departmentData.name" :disabled="saving" maxlength="255" required/>
        </b-form-group>
        <b-form-group description="Référence unique utilisée dans les codes des offres et projets." label="Code"
                      label-for="department-code">
          <b-form-input id="department-code" v-model="departmentData.code" :disabled="saving" maxlength="5" required/>
        </b-form-group>
        <b-button :disabled="saving || !departmentData.name.trim() || !departmentData.code.trim()" type="submit"
                  variant="primary">
          <b-spinner v-if="saving" small/>
          Enregistrer
        </b-button>
        <b-button :disabled="saving" class="ml-1" variant="outline-secondary" @click="showCreateModal = false">Annuler
        </b-button>
      </b-form>
    </b-modal>
    <b-modal v-model="showDeleteModal" :hide-header-close="deleting" :no-close-on-backdrop="deleting"
             :no-close-on-esc="deleting"
             hide-footer title="Supprimer le département" @hide="preventDeleteClose">
      <p v-if="departmentToDelete">Voulez-vous supprimer le département <strong>{{ departmentToDelete.name }}
        ({{ departmentToDelete.code }})</strong> ?</p>
      <b-button :disabled="deleting" variant="danger" @click="deleteDepartment">
        <b-spinner v-if="deleting" small/>
        Supprimer
      </b-button>
      <b-button :disabled="deleting" class="ml-1" variant="outline-secondary" @click="showDelete = false">Annuler
      </b-button>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BAlert,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BFormInput,
  BLink,
  BSpinner,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import useDepartmentsList from "./useDepartmentsList";
import {computed} from "@vue/composition-api";
import store from "@/store";

export default {
  name: "DepartmentsList",
  components: {
    BTooltip,
    BCard,
    BRow,
    BCol,
    BAlert,
    BFormInput,
    BForm,
    BFormGroup,
    BButton,
    BTable,
    BSpinner,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormCheckbox,

    vSelect,
  },
  setup() {

    const {
      editingUid,
      departmentData,
      showCreateModal,
      showDeleteModal,
      openCreateModal,
      openEditModal,
      openDeleteModal,
      preventDeleteClose,
      preventSavingClose,
      saveDepartment,
      fetchDepartmentsList,
      tableColumns,
      perPage,
      currentPage,
      totalDepartments,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDepartmentsListTable,
      refetchData,
      deleteDepartment,
      loading,
      saving,
      deleting,
      departmentToDelete,
    } = useDepartmentsList();

    const errors = computed(() => store.getters.errors);


    return {
      editingUid,
      departmentData,
      loading,
      saving,
      deleting,
      errors,
      showCreateModal,
      showDeleteModal,
      openCreateModal,
      openEditModal,
      preventDeleteClose,
      preventSavingClose,
      openDeleteModal,
      saveDepartment,
      fetchDepartmentsList,
      tableColumns,
      perPage,
      currentPage,
      totalDepartments,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDepartmentsListTable,
      refetchData,
      deleteDepartment,
      departmentToDelete,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
